import { Checkbox, Table, TableBody, TableCell, TableRow } from "@mui/material";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import { memo } from "react";
import DataTableHeadCell from "./DataTableHeadCell";
import DataTableBodyCell from "./DataTableBodyCell";

export const TableData = memo(
  ({
    tableInstance,
    initialRowIndex,
    displayedRows,
    isSelection,
    isMultiSelect,
    selectedAll,
    isSorted,
    selectedRows,
    fixedWidth,
    noEndBorder,
    setSelectedRows,
    onRowClick,
    onRowRedirect,
    setSelectedAll,
    setInitialRowIndex,
    setCurrentRowIndex,
  }) => {
    const { getTableProps, getTableBodyProps, headerGroups, prepareRow, rows, page } = tableInstance;

    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    // A function that sets the sorted value for the table
    const setSortedValue = (column) => {
      let sortedValue;

      if (isSorted && column.isSorted) {
        sortedValue = column.isSortedDesc ? "desc" : "asce";
      } else if (isSorted) {
        sortedValue = "none";
      } else {
        sortedValue = false;
      }

      return sortedValue;
    };

    const handleSelectRowBetween = (index) => {
      let diff = initialRowIndex - index;
      let selected;
      if (diff < 0) {
        selected = displayedRows.slice(initialRowIndex, index + 1);
      } else {
        selected = displayedRows.slice(index, initialRowIndex + 1);
      }
      setSelectedRows((previousSelected) => [...new Set([...previousSelected, ...selected?.map((select) => select.original.id)])]);
    };

    const toggleSelectAll = (selectedAll) => {
      // if (selectedRows.length === page.length) {
      if (!selectedAll) {
        setSelectedRows([]); // Deselect all
      } else {
        setSelectedRows(displayedRows.map((row) => row.original.id)); // Select all on current page
      }
      setSelectedAll(selectedAll);
    };

    const toggleRowSelection = (rowId) => {
      if (typeof setSelectedRows !== "function") return;
      setSelectedRows((prevSelectedRows) => {
        // Ensure prevSelectedRows is an array
        const updatedRows = Array.isArray(prevSelectedRows) ? prevSelectedRows : [];

        if (isMultiSelect) {
          // In multi-select mode, toggle the row selection
          return updatedRows.includes(rowId)
            ? updatedRows.filter((id) => id !== rowId) // Remove rowId if it's already selected
            : [...updatedRows, rowId]; // Add rowId to selected rows if not already selected
        } else {
          // In single-select mode, select only the clicked row
          return updatedRows[0] === rowId ? [] : [rowId];
        }
      });
    };

    return (
      <Table {...getTableProps()}>
        <MDBox component="thead" sx={{ position: "sticky", top: 0, zIndex: 2 }}>
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {/* Header Checkbox */}
              {isSelection && isMultiSelect && (
                <TableCell
                  padding="checkbox"
                  sx={({ palette: { light, background }, borders: { borderWidth } }) => ({
                    borderBottom: `${borderWidth[1]} solid ${light.main}`,
                    backgroundColor: darkMode ? background.card : background.paper,
                  })}
                >
                  <Checkbox
                    indeterminate={selectedRows.length > 0 && selectedRows.length < displayedRows.length}
                    checked={selectedAll}
                    onChange={() => toggleSelectAll(!selectedAll)}
                  />
                </TableCell>
              )}

              {headerGroup.headers.map((column) => (
                <DataTableHeadCell
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                  width={column.width ? column.width : "auto"}
                  align={column.align ? column.align : "left"}
                  canSort={column.canSort}
                  sorted={setSortedValue(column)}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {displayedRows.map((row, key) => {
            prepareRow(row);
            const isRowSelected =
              Array.isArray(selectedRows) && isMultiSelect ? selectedRows.includes(row.original.id) : selectedRows == row.original.id;
            return (
              <TableRow
                {...row.getRowProps()}
                onClick={(e) => {
                  // Only trigger row selection if it's not the checkbox being clicked
                  if (!e.target.closest("input[type='checkbox']")) {
                    if (onRowRedirect) {
                      onRowRedirect(row.original.id);
                    } else {
                      toggleRowSelection(row.original.id);
                      if (selectedRows?.length <= 0) {
                        if(typeof onRowClick === 'function'){
                          onRowClick(row.original.id);
                        }
                        setInitialRowIndex(key);
                        setCurrentRowIndex(key);
                      } else if (e.shiftKey) {
                        handleSelectRowBetween(key);
                      }
                    }
                  }
                }}
                sx={{
                  cursor: "pointer",
                  // Hover background color when the row is not selected
                  "&:hover": {
                    backgroundColor: (theme) =>
                      selectedRows && isMultiSelect
                        ? selectedRows.includes(row.original.id)
                        : selectedRows == row.original.id
                        ? "rgba(0, 0, 255, 0.2)" // Maintain the selected color when hovered
                        : "rgba(0, 0, 0, 0.04)", // Hover color
                  },
                  // Background color when the row is selected
                  backgroundColor:
                    selectedRows && isMultiSelect
                      ? selectedRows.includes(row.original.id)
                      : selectedRows == row.original.id
                      ? "rgba(0, 0, 255, 0.2)" // Selected color
                      : "", // Default background color
                }}
              >
                {isSelection && isMultiSelect && (
                  <TableCell className="checkbox-filter">
                    <Checkbox
                      checked={isRowSelected}
                      onClick={(e) => {
                        toggleRowSelection(row.original.id);
                        if (selectedRows.length <= 0) {
                          setInitialRowIndex(key);
                          setCurrentRowIndex(key);
                        } else if (e.shiftKey) {
                          handleSelectRowBetween(key);
                        }
                      }}
                    />
                  </TableCell>
                )}
                {row.cells.map((cell) => (
                  <DataTableBodyCell
                    noBorder={noEndBorder && rows.length - 1 === key}
                    align={cell.column.align ? cell.column.align : "left"}
                    {...cell.getCellProps()}
                    fixedWidth={fixedWidth}
                  >
                    {cell.render("Cell")}
                  </DataTableBodyCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  }
);
